import {
  Button,
  Text,
  VStack,
  Box,
  Tag,
  useDisclosure,
  ButtonGroup,
  IconButton,
  Icon,
  HStack,
  Spacer,
  Spinner,
  NativeSelect,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useStores } from "../../../../shared/stores/RootStore";
import { toast } from "../../../../shared/views/CustomToaster";
import { AddIcon, DeleteIcon, SaveIcon } from "../../../../shared/views/icons";
import { CustomTooltip } from "../../../../shared/views/CustomTooltip";
import { AddHlaForecastActionModal } from "./AddHlaForecastActionModal";
import { HlaForecastActionTooltip } from "./HlaForecastActionTooltip";
import { CreateOrUpdateHlaForecastScenarioModal } from "./CreateOrUpdateHlaForecastScenarioModal";

const i18prefix = "map:forecast.display";

const HlaForecastControlsBase: React.FC = () => {
  const { hlaForecastStore, projectStore } = useStores();

  const addActionModal = useDisclosure();
  const createOrUpdateModal = useDisclosure();
  const selectedProject = projectStore.getSelected();

  const onRemoveChange = (changeIndex: number) => {
    hlaForecastStore.removeActionByIndex(changeIndex);
  };

  const onDelete = () => {
    if (
      window.confirm(
        t(`${i18prefix}.delete.confirm`, {
          name: hlaForecastStore.selectedScenario!.name,
        }),
      )
    ) {
      hlaForecastStore
        .deleteScenario(hlaForecastStore.selectedScenario!.id)
        .then(() => toast("success", t(`${i18prefix}.delete.success`)))
        .catch(() => toast("error", t(`${i18prefix}.delete.error`)));
    }
  };

  return (
    <>
      <VStack align="stretch">
        <HStack>
          <Text fontSize="sm">{t(`${i18prefix}.title`)}</Text>
          <Spacer />
          {hlaForecastStore.computingValues && (
            <HStack>
              <Spinner color="primary.500" size="xs" />
              <Text fontSize="xs">{t(`${i18prefix}.loading`)}</Text>
            </HStack>
          )}
        </HStack>

        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            fontSize={"sm"}
            value={hlaForecastStore.selectedScenario?.id ?? ""}
            onChange={(e) =>
              hlaForecastStore.setSelectedScenario(e.target.value)
            }
            placeholder={
              hlaForecastStore.scenarios.length
                ? t(`${i18prefix}.select`)
                : t(`${i18prefix}.selectEmpty`)
            }
          >
            {hlaForecastStore.scenarios.map((scenario) => (
              <option value={scenario.id} key={scenario.id}>
                {scenario.name}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>

        {selectedProject?.permissions.canManageResults && (
          <>
            <ButtonGroup
              size={"sm"}
              colorPalette="primary"
              variant="outline"
              fontSize="sm"
            >
              <Button
                flexGrow={1}
                variant="outline"
                fontSize={"sm"}
                size="sm"
                colorPalette="primary"
                onClick={addActionModal.onOpen}
              >
                <AddIcon /> {t(`${i18prefix}.addAction`)}
              </Button>
              <IconButton
                onClick={createOrUpdateModal.onOpen}
                disabled={!hlaForecastStore.actions.length}
              >
                <Icon as={SaveIcon} />
              </IconButton>
              <IconButton
                disabled={!hlaForecastStore.selectedScenario}
                onClick={onDelete}
              >
                <DeleteIcon />
              </IconButton>
            </ButtonGroup>
          </>
        )}

        <Box m={-1}>
          {hlaForecastStore.actions.map((action, i) => (
            <CustomTooltip
              key={i}
              content={<HlaForecastActionTooltip action={action} />}
            >
              <Tag.Root m={1} py={1} fontSize="xs">
                <Tag.Label>
                  {Object.entries(action.attributeChanges)
                    .map(([attributeName, value]) => {
                      const [name, side] = attributeName.split(".");
                      return `${t(`shared:hlaAttribute.${name}`, {
                        unit: projectStore.speedUnitStr,
                      })}${side ? ` (${side})` : ""} = ${value}`;
                    })
                    .join(", ")}
                </Tag.Label>

                {selectedProject?.permissions.canManageResults && (
                  <Tag.EndElement>
                    <Tag.CloseTrigger onClick={() => onRemoveChange(i)} />
                  </Tag.EndElement>
                )}
              </Tag.Root>
            </CustomTooltip>
          ))}
        </Box>
      </VStack>

      {createOrUpdateModal.open && (
        <CreateOrUpdateHlaForecastScenarioModal
          onClose={createOrUpdateModal.onClose}
        />
      )}

      {addActionModal.open && (
        <AddHlaForecastActionModal onClose={addActionModal.onClose} />
      )}
    </>
  );
};

export const HlaForecastControls = observer(HlaForecastControlsBase);
